import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import type { LicenseStatus } from '@shared/license-types'
import { useApi } from './ApiContext'

interface LicenseContextValue {
  status: LicenseStatus | null
  loading: boolean
  refresh: () => Promise<void>
}

const LicenseContext = createContext<LicenseContextValue>({
  status: null,
  loading: true,
  refresh: async () => {},
})

export function LicenseProvider({ children }: { children: ReactNode }) {
  const api = useApi()
  const [status, setStatus] = useState<LicenseStatus | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const s = await api.getLicenseStatus()
      setStatus(s)
    } catch (err) {
      console.error('Failed to check license:', err)
      setStatus({
        isValid: false,
        isActivated: false,
        isExpired: false,
        needsActivation: true,
        daysUntilExpiry: 0,
        licenseType: null,
        license: null,
        gracePeriodDays: 0,
        contactName: '',
        maxDashboards: null,
        error: 'Failed to check license status.',
      })
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    const unsubscribe = api.onLicenseCheck((s) => setStatus(s))
    return unsubscribe
  }, [api])

  return (
    <LicenseContext.Provider value={{ status, loading, refresh }}>
      {children}
    </LicenseContext.Provider>
  )
}

export function useLicense() {
  return useContext(LicenseContext)
}
